<template>
    <div class="pa-4">
      <!-- Header -->
    <div class="ww-page-header">
      <div class="ww-page-heading">
        <div class="ww-page-title">Inspections</div>
        <div class="ww-page-subtitle">View and manage all of your inspections</div>
      </div>
      <div class="d-flex ga-2 flex-wrap">
        <!-- Temporarily hidden for customer release pending SI workbook validation.
        <v-btn color="success" variant="outlined" @click="exportWorkbook" :loading="exportingWorkbook"
          :disabled="loading || !filteredInspections.length"
          title="Generate a Sport Ireland workbook for all inspections matching the current filters and search">
          <v-icon start icon="mdi-file-excel" /> Generate SI Workbook
        </v-btn>
        -->
        <v-btn color="success" variant="outlined" @click="exportToExcel" :loading="exporting">
          <v-icon start icon="mdi-file-excel" /> Export to Excel
        </v-btn>
        <v-btn color="primary" @click="onAddInspection">
          <v-icon start icon="mdi-plus" /> Create Inspection
        </v-btn>
      </div>
    </div>

    <!-- Filters -->
    <v-alert v-if="workbookError" type="error" variant="tonal" class="mb-4" closable @click:close="workbookError = ''">{{ workbookError }}</v-alert>
    <div class="d-flex ga-3 mb-4 flex-wrap align-center">
      <v-select
        v-model="statusFilter"
        :items="inspectionStatuses"
        item-title="name"
        item-value="lookupDataId"
        label="Filter Status"
        clearable
        density="comfortable"
        style="max-width: 200px"
      />
      <v-select
        v-model="zoneFilter"
        :items="zones"
        item-title="name"
        item-value="zoneId"
        label="Filter Zone"
        clearable
        density="comfortable"
        style="max-width: 200px"
      />
      <v-spacer />
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Search inspections"
        density="comfortable"
        clearable
        style="max-width: 300px"
      />
    </div>

    <!-- Grid -->
    <v-data-table
      :headers="headers"
      :items="filteredInspections"
      :loading="loading"
      class="elevation-1 inspections-table"
      item-key="inspectionId"
      density="comfortable"
      loading-text="Loading inspections…"
      no-data-text="No inspections found."
      height="500"
      fixed-header
      :sort-by="[{ key: 'dueDate', order: 'desc' }]"
      @click:row="onRowClick"
    >
      <template #item.name="{ item }">
        <span class="text-primary">{{ item.name }}</span>
      </template>

      <template #item.inspectionTypeName="{ item }">
        {{ item.inspectionTypeName || "Not recorded" }}
      </template>

      <template #item.ownerFK="{ item }">
        <div class="submitted-by-cell">
          <v-avatar size="32" class="submitted-by-avatar bg-avatar">
            <v-img :src="getUserIconUrl(item.ownerIconId)" />
          </v-avatar>
          <div class="submitted-by-text">
            {{ getInspectionUserDisplayName(item) }}
          </div>
        </div>
      </template>

      <template #item.teams="{ item }">
        <div class="d-flex flex-wrap ga-1">
          <v-tooltip
            v-for="team in item.teamIds"
            :key="team"
            location="top"
            open-delay="300"
            @update:model-value="(val) => val && loadTeamMembers(team)"
          >
            <template #activator="{ props }">
              <v-chip
                v-bind="props"
                color="blue-lighten-4"
                size="small"
                class="ma-1"
                variant="elevated"
              >
                {{ getTeamName(team) }}
              </v-chip>
            </template>
            <span>{{ teamUsersCache[team]?.join(", ") || "Loading..." }}</span>
          </v-tooltip>
        </div>
      </template>

      <template #item.lastUpdated="{ item }">
        {{ formatDateTime(item.lastUpdated) }}
      </template>

      <template #item.dueDate="{ item }">
        <div v-if="item.dueDate" class="d-flex align-center">
          <v-chip
            :color="getDueDateColor(item.dueDate)"
            variant="elevated"
            size="small"
            class="mr-1"
          >
            <v-icon start size="14">{{ getDueDateIcon(item.dueDate) }}</v-icon>
            {{ formatDate(item.dueDate) }}
          </v-chip>
          <span v-if="isOverdue(item.dueDate)" class="text-caption text-error ml-1">Overdue</span>
          <span v-else-if="isDueSoon(item.dueDate)" class="text-caption text-warning ml-1">Due Soon</span>
        </div>
        <span v-else class="text-grey">No due date</span>
      </template>

      <template #item.statusFK="{ item }">
        <v-chip :color="item.statusColour" variant="elevated" size="small">
          {{ item.statusFK }}
        </v-chip>
      </template>

      <template #item.actions="{ item }">
        <div class="d-flex align-center ga-1" @click.stop>
          <v-btn
            icon
            size="x-small"
            color="primary"
            :title="`View findings for ${item.name} on map`"
            @click.stop="onViewMap(item)"
          >
            <v-icon size="20">mdi-map</v-icon>
          </v-btn>
          <v-btn icon size="x-small" @click.stop="onEditInspection(item)">
            <v-icon size="20">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon size="x-small" color="secondary" :title="`Repeat ${item.name}`" @click.stop="openRepeatDialog(item)">
            <v-icon size="20">mdi-content-copy</v-icon>
          </v-btn>
          <v-btn icon size="x-small" color="error" @click.stop="confirmDeleteInspection(item)">
            <v-icon size="20">mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </div>

  <!-- Create/Edit Inspection Modal -->
  <v-dialog v-model="showModal" persistent max-width="500px">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6 font-weight-bold">
          {{ isEditing ? "Edit Inspection" : "Create New Inspection" }}
        </span>
        <v-btn flat icon @click="showModal = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" lazy-validation>
          <v-text-field
            v-model="editInspectionData.name"
            label="Description"
            placeholder="Type Here..."
            required
          >
            <template #append>
              <audit-field-timeline
                v-if="isEditing && editInspectionData.inspectionId"
                entity-type="Inspection"
                :record-id="editInspectionData.inspectionId"
                field-key="Name"
                field-label="Description"
              />
            </template>
          </v-text-field>
          <v-textarea
            v-model="editInspectionData.description"
            label="Details"
            placeholder="Type Here..."
            auto-grow
            required
          >
            <template #append>
              <audit-field-timeline
                v-if="isEditing && editInspectionData.inspectionId"
                entity-type="Inspection"
                :record-id="editInspectionData.inspectionId"
                field-key="Description"
                field-label="Details"
              />
            </template>
          </v-textarea>
          <v-select
            v-model="editInspectionData.zoneId"
            label="Zone"
            :items="zones"
            item-title="name"
            item-value="zoneId"
            required
          >
            <template #append>
              <audit-field-timeline
                v-if="isEditing && editInspectionData.inspectionId"
                entity-type="Inspection"
                :record-id="editInspectionData.inspectionId"
                field-key="ZoneNameFK"
                field-label="Zone"
              />
            </template>
          </v-select>
          <v-select
            v-model="editInspectionData.ownerId"
            label="Owner"
            :items="users"
            item-title="userName"
            item-value="id"
            required
          >
            <template #append>
              <audit-field-timeline
                v-if="isEditing && editInspectionData.inspectionId"
                entity-type="Inspection"
                :record-id="editInspectionData.inspectionId"
                field-key="OwnerFK"
                field-label="Owner"
              />
            </template>
          </v-select>
          <v-select
            v-model="editInspectionData.inspectionTypeId"
            label="Inspection Type (Why)"
            :items="inspectionTypeOptions"
            item-title="name"
            item-value="lookupDataId"
            :rules="[v => isEditing || !!v || 'Inspection Type (Why) is required']"
            :error-messages="inspectionTypesError"
          />
          <v-select
            v-model="editInspectionData.statusId"
            label="Status"
            :items="inspectionStatuses"
            item-title="name"
            item-value="lookupDataId"
            :rules="[v => !!v || 'Status is required']"
          >
            <template #append>
              <audit-field-timeline
                v-if="isEditing && editInspectionData.inspectionId"
                entity-type="Inspection"
                :record-id="editInspectionData.inspectionId"
                field-key="StatusFK"
                field-label="Status"
              />
            </template>
          </v-select>
          <v-text-field
            v-model="editInspectionData.dueDate"
            type="date"
            label="Due Date"
            variant="outlined"
            prepend-inner-icon="mdi-calendar-clock"
          >
            <template #append>
              <audit-field-timeline
                v-if="isEditing && editInspectionData.inspectionId"
                entity-type="Inspection"
                :record-id="editInspectionData.inspectionId"
                field-key="DueDate"
                field-label="Due Date"
              />
            </template>
          </v-text-field>

          <div class="text-caption text-medium-emphasis mb-1">Assign To Team/s</div>
          <v-select
            v-model="teamIds"
            :items="teams"
            item-title="name"
            item-value="id"
            multiple
            clearable
            density="comfortable"
            class="mb-3"
          >
            <template #selection="{ item, index }">
              <v-chip
                :key="index"
                class="ma-1"
                color="blue-lighten-4"
                size="small"
                closable
                variant="elevated"
                @click:close="teamIds.splice(index, 1)"
              >
                {{ item.raw.name }}
              </v-chip>
            </template>
          </v-select>

          <div class="text-caption text-medium-emphasis mb-1">Assign Forms to Inspection</div>
          <v-select
            v-model="selectedFormIds"
            :items="forms"
            item-title="name"
            item-value="formId"
            multiple
            clearable
            density="comfortable"
          >
            <template #selection="{ item, index }">
              <v-chip
                :key="index"
                class="ma-1"
                color="blue-lighten-4"
                size="small"
                closable
                variant="elevated"
                @click:close="selectedFormIds.splice(index, 1)"
              >
                {{ item.raw.name }}
              </v-chip>
            </template>
          </v-select>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="showModal = false">Cancel</v-btn>
        <v-btn color="green" @click="saveInspection">
          {{ isEditing ? "Update" : "Create" }} Inspection
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="showRepeatDialog" persistent max-width="760px">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Repeat Inspection</span>
        <v-btn flat icon @click="showRepeatDialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle>Copy {{ repeatSource?.name }} into future suggested dates</v-card-subtitle>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="4">
            <v-text-field v-model.number="repeatOptions.count" type="number" min="1" max="100" label="Number of inspections" />
          </v-col>
          <v-col cols="12" sm="4">
            <v-select v-model="repeatOptions.frequency" :items="frequencyOptions" label="Cycle type" />
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field v-model.number="repeatOptions.interval" type="number" min="1" label="Repeat every" :suffix="frequencySuffix" />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="repeatOptions.firstDate" type="date" label="First suggested date" />
          </v-col>
          <v-col v-if="repeatOptions.frequency === 'Week'" cols="12" sm="6">
            <v-select v-model="repeatOptions.dayOfWeek" :items="weekdays" item-title="title" item-value="value" label="Day of week" />
          </v-col>
          <v-col v-if="repeatOptions.frequency === 'Custom'" cols="12" sm="6">
            <v-text-field v-model.number="repeatOptions.customDays" type="number" min="1" label="Fixed number of days" />
          </v-col>
          <v-col v-if="repeatOptions.frequency === 'Month'" cols="12" sm="6">
            <v-select v-model="repeatOptions.monthPosition" :items="monthPositions" item-title="title" item-value="value" label="Position in month" />
          </v-col>
          <v-col v-if="repeatOptions.frequency === 'Month' && repeatOptions.monthPosition === 'Custom'" cols="12" sm="6">
            <v-text-field v-model.number="repeatOptions.monthDay" type="number" min="1" max="31" label="Day of month" />
          </v-col>
        </v-row>

        <v-alert v-if="repeatValidationError" type="warning" variant="tonal" class="mb-3">{{ repeatValidationError }}</v-alert>
        <div class="text-subtitle-2 mb-2">Preview</div>
        <v-table density="compact" height="240px" fixed-header>
          <thead><tr><th>#</th><th>Name</th><th>Suggested date</th></tr></thead>
          <tbody>
            <tr v-for="row in repeatPreview" :key="row.sequence">
              <td>{{ row.sequence }}</td><td>{{ row.name }}</td><td>{{ formatDate(row.date) }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="showRepeatDialog = false">Cancel</v-btn>
        <v-btn color="primary" :disabled="!!repeatValidationError" :loading="repeatingInspection" @click="createInspectionSeries">
          Create {{ repeatPreview.length }} inspections
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="showDeleteConfirm" max-width="500px" persistent>
    <v-card>
      <v-card-title class="text-error d-flex align-center">
        <v-icon class="mr-2">mdi-alert</v-icon>
        Confirm Delete
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <p class="text-body-1">
          Are you sure you want to delete <strong>{{ inspectionToDelete?.name }}</strong>?
        </p>
        <p class="text-body-2 text-grey mt-2">
          This action cannot be undone. The inspection will be permanently removed.
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="showDeleteConfirm = false">Cancel</v-btn>
        <v-btn color="error" variant="flat" @click="deleteInspection" :loading="deletingInspection">
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { downloadInspectionWorkbook } from '@/api/sportIreland';
import { getAll, getById, deleteItem, createInspection, repeatInspection, updateInspection, getDueSoon, exportInspectionsToExcel } from "@/api/inspections";
import { fetchUsers } from "@/api/dropdowns";
import { fetchZones } from "@/api/zones";
import { getTeams, getUsersInTeam } from "@/api/teams";
import { fetchFormsDropdown } from "@/api/formsTemplates";
import { getLookupDataByType } from "@/api/lookups";
import AuditFieldTimeline from "@/components/audit/AuditFieldTimeline.vue";

export default {
  name: "InspectionList",
  components: { AuditFieldTimeline },
  data() {
    return {
      headers: [
        { title: "Description", key: "name" },
        { title: "Inspection Type (Why)", key: "inspectionTypeName" },
        { title: "Zone", key: "zoneNameFK" },
        { title: "Owner", key: "ownerFK" },
        { title: "Teams", key: "teams", sortable: false },
        { title: "Due Date", key: "dueDate" },
        { title: "Last Updated", key: "lastUpdated" },
        { title: "Status", key: "statusFK" },
        { title: "Findings", key: "findingsCount" },
        { title: "Actions", key: "actions", sortable: false },
      ],
      inspections: [],
      loading: false,
      exporting: false,
      exportingWorkbook: false,
      workbookError: '',
      search: "",
      statusFilter: null,
      zoneFilter: null,
      showModal: false,
      loadingInspectionForEdit: false,
      isEditing: false,
      showDeleteConfirm: false,
      inspectionToDelete: null,
      deletingInspection: false,
      showRepeatDialog: false,
      repeatingInspection: false,
      repeatSource: null,
      frequencyOptions: ["Day", "Week", "Month", "Quarter", "Year", "Custom"],
      weekdays: [
        { title: "Sunday", value: 0 }, { title: "Monday", value: 1 },
        { title: "Tuesday", value: 2 }, { title: "Wednesday", value: 3 },
        { title: "Thursday", value: 4 }, { title: "Friday", value: 5 },
        { title: "Saturday", value: 6 },
      ],
      monthPositions: [
        { title: "Same date as original", value: "SameDay" },
        { title: "Start of month", value: "Start" },
        { title: "Mid-month (15th)", value: "Mid" },
        { title: "End of month", value: "End" },
        { title: "Custom day", value: "Custom" },
      ],
      repeatOptions: {
        count: 6, frequency: "Month", interval: 1, firstDate: "",
        dayOfWeek: 1, customDays: 30, monthPosition: "SameDay", monthDay: 1,
      },
      users: [],
      zones: [],
      teams: [],
      teamIds: [],
      teamUsersCache: {},
      forms: [],
      selectedFormIds: [],
      inspectionStatuses: [],
      inspectionTypes: [],
      inspectionTypesError: "",
      editInspectionData: {
        inspectionId: null,
        name: "",
        description: "",
        zoneId: null,
        ownerId: null,
        owner: null,
        startDate: null,
        dueDate: null,
        teamIds: null,
        statusId: null,
        inspectionTypeId: null,
      },
    };
  },
  computed: {
    inspectionTypeOptions() {
      const options = [...this.inspectionTypes];
      const selected = this.editInspectionData;
      if (selected.inspectionTypeId && !options.some(t => t.lookupDataId === selected.inspectionTypeId))
        options.push({ lookupDataId: selected.inspectionTypeId, name: selected.inspectionTypeName || 'Previously selected type' });
      return options;
    },
    frequencySuffix() {
      if (this.repeatOptions.frequency === "Custom") return "cycle(s)";
      return `${this.repeatOptions.frequency.toLowerCase()}(s)`;
    },
    repeatValidationError() {
      const o = this.repeatOptions;
      if (!o.firstDate) return "Choose the first suggested date.";
      if (!Number.isInteger(Number(o.count)) || o.count < 1 || o.count > 100) return "Choose between 1 and 100 inspections.";
      if (!Number.isInteger(Number(o.interval)) || o.interval < 1) return "Repeat interval must be at least 1.";
      if (o.frequency === "Custom" && (!Number.isInteger(Number(o.customDays)) || o.customDays < 1)) return "Fixed days must be at least 1.";
      if (o.frequency === "Month" && o.monthPosition === "Custom" && (o.monthDay < 1 || o.monthDay > 31)) return "Day of month must be between 1 and 31.";
      return "";
    },
    repeatPreview() {
      if (this.repeatValidationError || !this.repeatSource) return [];
      const rows = [];
      const o = this.repeatOptions;
      let first = this.parseLocalDate(o.firstDate);
      if (o.frequency === "Week") {
        const offset = (Number(o.dayOfWeek) - first.getDay() + 7) % 7;
        first.setDate(first.getDate() + offset);
      }
      if (o.frequency === "Month") first = this.applyMonthPosition(first, first.getDate());
      for (let i = 0; i < Number(o.count); i += 1) {
        let date = new Date(first.getFullYear(), first.getMonth(), first.getDate());
        const step = i * Number(o.interval);
        if (o.frequency === "Day") date.setDate(date.getDate() + step);
        else if (o.frequency === "Week") date.setDate(date.getDate() + step * 7);
        else if (o.frequency === "Month") {
          date = new Date(first.getFullYear(), first.getMonth() + step, 1);
          date = this.applyMonthPosition(date, first.getDate());
        }
        else if (o.frequency === "Quarter") date.setMonth(date.getMonth() + step * 3);
        else if (o.frequency === "Year") date.setFullYear(date.getFullYear() + step);
        else date.setDate(date.getDate() + i * Number(o.customDays));
        rows.push({ sequence: i + 1, date, name: this.repeatedInspectionName(this.repeatSource.name, date, o.frequency) });
      }
      return rows;
    },
    filteredInspections() {
      return this.inspections.filter((i) => {
        if (this.statusFilter && i.statusId !== this.statusFilter) return false;
        if (this.zoneFilter && i.zoneId !== this.zoneFilter) return false;
        if (this.search) {
          const term = this.search.toLowerCase();
          if (!Object.values(i).some((val) => String(val).toLowerCase().includes(term))) return false;
        }
        return true;
      });
    },
  },
  methods: {
    async exportWorkbook() {
      if (this.loading || this.exportingWorkbook || !this.filteredInspections.length) return;
      const ids = this.filteredInspections.map(i => i.inspectionId);
      this.exportingWorkbook = true;
      this.workbookError = '';
      let url;
      let link;
      try {
        const response = await downloadInspectionWorkbook(ids);
        url = URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        link = document.createElement('a');
        link.href = url;
        link.download = `SportIreland_Inspections_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        let detail;
        try {
          const body = error.response?.data;
          detail = body instanceof Blob ? JSON.parse(await body.text()) : body;
        } catch { /* Show the fallback when the response is not JSON. */ }
        this.workbookError = detail?.error || 'Unable to generate the workbook. Please try again.';
      } finally {
        link?.remove();
        if (url) setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.exportingWorkbook = false;
      }
    },
    parseLocalDate(value) {
      const [year, month, day] = value.split("-").map(Number);
      return new Date(year, month - 1, day);
    },
    applyMonthPosition(date, sameDay = date.getDate()) {
      const result = new Date(date.getFullYear(), date.getMonth(), 1);
      const last = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
      const position = this.repeatOptions.monthPosition;
      const desired = position === "Start" ? 1 : position === "Mid" ? 15 : position === "End" ? last
        : position === "Custom" ? Number(this.repeatOptions.monthDay) : sameDay;
      result.setDate(Math.min(Math.max(desired, 1), last));
      return result;
    },
    repeatedInspectionName(baseName, date, frequency) {
      let suffix;
      if (frequency === "Month") suffix = date.toLocaleDateString("en-IE", { month: "long", year: "numeric" });
      else if (frequency === "Quarter") suffix = `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
      else if (frequency === "Year") suffix = String(date.getFullYear());
      else suffix = date.toLocaleDateString("en-IE", { day: "2-digit", month: "short", year: "numeric" });
      return `${baseName} – ${suffix}`;
    },
    openRepeatDialog(item) {
      const sourceDate = this.toIsoDate(item.dueDate) || new Date().toISOString().slice(0, 10);
      const parsed = this.parseLocalDate(sourceDate);
      this.repeatSource = item;
      this.repeatOptions = {
        count: 6, frequency: "Month", interval: 1, firstDate: sourceDate,
        dayOfWeek: parsed.getDay(), customDays: 30, monthPosition: "SameDay", monthDay: parsed.getDate(),
      };
      this.showRepeatDialog = true;
    },
    async createInspectionSeries() {
      if (this.repeatValidationError || !this.repeatSource) return;
      this.repeatingInspection = true;
      try {
        const payload = { ...this.repeatOptions, firstDate: `${this.repeatOptions.firstDate}T00:00:00` };
        const result = await repeatInspection(this.repeatSource.inspectionId, payload);
        const count = result?.inspections?.length || this.repeatPreview.length;
        this.$toast?.success?.(`${count} inspections created successfully`);
        this.showRepeatDialog = false;
        await this.fetchInspections();
      } catch (err) {
        this.$toast?.error?.(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to repeat inspection");
      } finally {
        this.repeatingInspection = false;
      }
    },
    async exportToExcel() {
      this.exporting = true;
      try {
        const response = await exportInspectionsToExcel();
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Inspections_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$toast?.success?.("Inspections exported successfully");
      } catch (error) {
        this.$toast?.error?.("Failed to export inspections");
      } finally {
        this.exporting = false;
      }
    },
    async fetchInspections() {
      this.loading = true;
      try {
        const resp = await getAll();
        this.inspections = resp.map((i) => {
          const teamNames = (i.teamIds || []).map((id) => {
            const team = this.teams.find((t) => t.id === id);
            return team ? team.name : `Team ${id}`;
          });
          return { ...i, teamNames, lastUpdated: i.lastUpdated || null };
        });
      } catch (err) {
        // handled silently
      } finally {
        this.loading = false;
      }
    },
    async loadTeams() {
      const list = await getTeams();
      this.teams = [{ id: 0, name: "All" }, ...list];
    },
    async loadForms() {
      this.forms = await fetchFormsDropdown();
    },
    async loadInspectionTypes() {
      try {
        this.inspectionTypes = (await getLookupDataByType("InspectionType")).filter(t => t.isActive !== false);
        this.inspectionTypesError = this.inspectionTypes.length ? "" : "No inspection types available in Setups.";
        if (this.showModal && !this.isEditing && !this.editInspectionData.inspectionTypeId)
          this.editInspectionData.inspectionTypeId = this.inspectionTypes.find(t => t.name === "Routine")?.lookupDataId ?? null;
      } catch {
        this.inspectionTypesError = "Could not load inspection types. Reopen this form to retry.";
      }
    },
    async loadInspectionStatuses() {
      try {
        const result = await getLookupDataByType("InspectionStatus");
        this.inspectionStatuses = result
          .filter((item) => item.isActive)
          .map((item) => ({ lookupDataId: item.lookupDataId, name: item.name }));
      } catch (err) {
        this.inspectionStatuses = [];
      }
    },
    getUserIconUrl(id) {
      const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
      return `/images/avatars/${key}.svg`;
    },
    isGuidLike(value) {
      return typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
    },
    getInspectionUserDisplayName(item) {
      const directName = item?.ownerFK || item?.ownerName || item?.submittedByFK;
      if (directName && !this.isGuidLike(directName)) {
        return directName;
      }

      const userId = item?.ownerId || item?.submittedBy;
      const matchedUser = (this.users || []).find((u) => {
        const id = u.id || u.userId || u.Id || u.UserId;
        return id && String(id) === String(userId);
      });

      if (matchedUser) {
        return matchedUser.userName || matchedUser.name || matchedUser.email || matchedUser.UserName || matchedUser.Email || "Unknown user";
      }

      return "Unknown user";
    },
    async fetchUsersAndZones() {
      this.users = await fetchUsers();
      this.zones = await fetchZones();
    },
    onAddInspection() {
      this.editInspectionData = {
        inspectionId: null,
        name: "",
        description: "",
        zoneId: null,
        owner: null,
        ownerId: null,
        startDate: null,
        dueDate: null,
        teamIds: null,
        statusId: null,
        inspectionTypeId: null,
      };
      this.editInspectionData.inspectionTypeId = this.inspectionTypes.find(t => t.name === "Routine")?.lookupDataId ?? null;
      this.loadInspectionTypes();
      this.teamIds = [];
      this.selectedFormIds = [];
      this.isEditing = false;
      this.showModal = true;
    },
    async onEditInspection(item) {
      this.loadInspectionTypes();
      this.loadingInspectionForEdit = true;
      try {
        const inspectionId = item.inspectionId || item.InspectionId;
        const full = await getById(inspectionId);
        if (!full) throw new Error("Inspection details were not returned by the server.");
        this.editInspectionData = {
          inspectionId: full.inspectionId,
          name: full.name,
          description: full.description,
          zoneId: full.zoneId,
          ownerId: full.ownerId,
          startDate: full.start,
          dueDate: this.toIsoDate(full.dueDate),
          teamIds: full.teamIds || [],
          statusId: full.statusId,
          inspectionTypeId: full.inspectionTypeId ?? null,
          inspectionTypeName: full.inspectionTypeName,
        };
        this.teamIds = [...this.editInspectionData.teamIds];
        this.selectedFormIds = full.formIds ? [...full.formIds] : [];
        this.isEditing = true;
        this.showModal = true;
      } catch (err) {
        const message = err?.response?.data?.error || err?.response?.data?.message ||
          (typeof err?.response?.data === "string" ? err.response.data : null) ||
          err?.message || "Failed to load inspection for editing";
        this.$toast?.error?.(message);
      } finally {
        this.loadingInspectionForEdit = false;
      }
    },
    async saveInspection() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        this.$toast?.error?.("Please fill in all required fields");
        return;
      }
      try {
        this.editInspectionData.teamIds = this.teamIds;
        this.editInspectionData.formIds = this.selectedFormIds;
        this.editInspectionData.owner = this.editInspectionData.ownerId;

        if (this.isEditing) {
          const updateData = {
            inspectionId: this.editInspectionData.inspectionId,
            name: this.editInspectionData.name,
            inspectionTypeId: this.editInspectionData.inspectionTypeId,
            description: this.editInspectionData.description,
            zoneId: this.editInspectionData.zoneId,
            ownerId: this.editInspectionData.ownerId,
            statusId: this.editInspectionData.statusId,
            dueDate: this.editInspectionData.dueDate ? new Date(this.editInspectionData.dueDate).toISOString() : null,
            teamIds: this.teamIds || [],
            formIds: this.selectedFormIds || [],
          };
          await updateInspection(this.editInspectionData.inspectionId, updateData);
        } else {
          let statusIdValue = parseInt(this.editInspectionData.statusId, 10);
          if (!statusIdValue && this.inspectionStatuses.length > 0) {
            statusIdValue = parseInt(this.inspectionStatuses[0].lookupDataId, 10);
          }
          if (!statusIdValue) statusIdValue = 1;

          const createData = {
            zoneId: this.editInspectionData.zoneId,
            owner: this.editInspectionData.ownerId,
            name: this.editInspectionData.name,
            inspectionTypeId: this.editInspectionData.inspectionTypeId,
            description: this.editInspectionData.description,
            created: this.editInspectionData.startDate ? new Date(this.editInspectionData.startDate).toISOString() : null,
            dueDate: this.editInspectionData.dueDate ? new Date(this.editInspectionData.dueDate).toISOString() : null,
            statusId: statusIdValue,
            teamIds: this.teamIds || [],
            formIds: this.selectedFormIds || [],
          };
          await createInspection(createData);
        }
        this.$toast?.success?.(`Inspection ${this.isEditing ? "updated" : "created"} successfully`);
        this.showModal = false;
        this.fetchInspections();
      } catch (err) {
        const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to save inspection";
        this.$toast?.error?.(errorMsg);
      }
    },
    onRowClick(_event, { item }) {
      const inspectionId = item.inspectionId || item.InspectionId;
      if (inspectionId) this.viewInspection(inspectionId);
    },
    viewInspection(id) {
      this.$router.push({ name: "InspectionEdit", params: { id } });
    },
    onViewMap(inspection) {
      const inspectionId = inspection.inspectionId || inspection.InspectionId;
      if (inspectionId) {
        this.$router.push({ name: "ViewMap", query: { inspectionId, filter: "findings" } });
      }
    },
    getTeamName(teamId) {
      const team = this.teams.find((t) => t.id === teamId);
      return team ? team.name : `Team ${teamId}`;
    },
    async loadTeamMembers(teamId) {
      if (this.teamUsersCache[teamId]) return;
      try {
        const users = await getUsersInTeam(teamId);
        this.teamUsersCache[teamId] = users.map((u) => u.userName || u.name);
      } catch (err) {
        this.teamUsersCache[teamId] = ["(Error)"];
      }
    },
    confirmDeleteInspection(inspection) {
      this.inspectionToDelete = inspection;
      this.showDeleteConfirm = true;
    },
    async deleteInspection() {
      if (!this.inspectionToDelete) return;
      this.deletingInspection = true;
      try {
        await deleteItem(this.inspectionToDelete.inspectionId);
        this.$toast?.success?.(`Inspection "${this.inspectionToDelete.name}" deleted successfully`);
        this.showDeleteConfirm = false;
        this.inspectionToDelete = null;
        await this.fetchInspections();
      } catch (err) {
        const errorMsg = err?.response?.data?.error || err?.message || "Failed to delete inspection";
        this.$toast?.error?.(errorMsg);
      } finally {
        this.deletingInspection = false;
      }
    },
    formatDateTime(dt) {
      if (!dt) return "";
      return new Date(dt).toLocaleString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
    },
    formatDate(dt) {
      if (!dt) return "";
      try {
        const date = new Date(dt);
        if (isNaN(date.getTime())) return "";
        return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
      } catch {
        return "";
      }
    },
    toIsoDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      return d.toISOString().split("T")[0];
    },
    isOverdue(dueDate) {
      if (!dueDate) return false;
      const due = new Date(dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      return due < today;
    },
    isDueSoon(dueDate) {
      if (!dueDate) return false;
      const due = new Date(dueDate);
      const today = new Date();
      const weekFromNow = new Date(today);
      weekFromNow.setDate(today.getDate() + 7);
      today.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      weekFromNow.setHours(0, 0, 0, 0);
      return due >= today && due <= weekFromNow;
    },
    getDueDateColor(dueDate) {
      if (this.isOverdue(dueDate)) return "error";
      if (this.isDueSoon(dueDate)) return "warning";
      return "success";
    },
    getDueDateIcon(dueDate) {
      if (this.isOverdue(dueDate)) return "mdi-alert-circle";
      if (this.isDueSoon(dueDate)) return "mdi-clock-alert";
      return "mdi-calendar-check";
    },
    async checkDueSoonInspections() {
      try {
        const dueSoon = await getDueSoon(7);
        if (!dueSoon?.length) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const overdue = dueSoon.filter((i) => {
          const due = new Date(i.dueDate);
          due.setHours(0, 0, 0, 0);
          return due < today;
        });
        const upcoming = dueSoon.filter((i) => {
          const due = new Date(i.dueDate);
          due.setHours(0, 0, 0, 0);
          return due >= today;
        });
        if (overdue.length > 0) {
          this.$toast?.warning?.(`${overdue.length} inspection(s) are overdue!`, { timeout: 5000 });
        } else if (upcoming.length > 0) {
          this.$toast?.info?.(`${upcoming.length} inspection(s) due within 7 days`, { timeout: 4000 });
        }
      } catch {
        // reminders are nice-to-have, fail silently
      }
    },
  },
  async mounted() {
    this.fetchUsersAndZones();
    this.loadInspectionStatuses();
    this.loadInspectionTypes();
    this.loadTeams().then(() => {
      this.loadForms();
      this.fetchInspections();
    });
    await this.checkDueSoonInspections();
  },
};
</script>

<style scoped>
.inspections-table :deep(.v-data-table__tr) {
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.inspections-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
.inspections-table :deep(.v-data-table__tr:active) {
  background-color: rgba(0, 0, 0, 0.08) !important;
}
.text-primary {
  color: rgb(25, 118, 210);
  font-weight: 500;
}

.submitted-by-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.submitted-by-avatar {
  flex-shrink: 0;
}

.submitted-by-text {
  font-weight: 500;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
